import { Button } from '@blueprintjs/core';
import { SearchPreset } from '@retro-ui/api-contract';
import {
  SearchLength,
  SearchSynthesisScale,
  SearchReactionConfidenceThreshold,
  M1_DEFAULT,
  MAX_PRICE_PER_GRAM_DEFAULT,
  MAX_PRICE_DEFAULT,
  MIN_N_PROVIDERS_DEFAULT,
  MIN_REFERENCE_COUNT_DEFAULT,
  MAX_N_ATOMS_DEFAULT,
} from '@retro-ui/shared';
import { push as changeLocation } from 'connected-react-router';
import _ from 'lodash';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';
import { DatasetValidation } from '@f/components/shared/search-parameter-form/services/validateDatasets';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { findReactionSearchPreferences } from '@f/services/reaction-search-preference/reaction-search-preference.actions';
import { BottomRightToaster } from '@f/services/toasters';

import PresetForm from './components/PresetForm';

export interface PresetEditable {
  name: string;
  isWebDefault: boolean;
  isApiDefault: boolean;
  searchParams: SearchPreset;
  datasetValidation?: DatasetValidation;
}

export interface Preset extends PresetEditable {
  id: string;
  ownerId: string;
}

const initialState: PresetEditable = {
  name: '',
  isWebDefault: false,
  isApiDefault: false,
  searchParams: {
    model: 'transformer',
    length: SearchLength.SHORT,
    synthesis_scale: SearchSynthesisScale.MILLIMOLAR,
    datasets: [],
    availability_tier: 3,
    reaction_confidence_threshold: SearchReactionConfidenceThreshold.HIGH,
    min_reference_count: MIN_REFERENCE_COUNT_DEFAULT,
    excluded_smiles: [],
    min_n_providers: MIN_N_PROVIDERS_DEFAULT,
    max_n_atoms: MAX_N_ATOMS_DEFAULT,
    max_price: MAX_PRICE_DEFAULT,
    max_price_per_gram: MAX_PRICE_PER_GRAM_DEFAULT,
  },
};

function getInitialState(): PresetEditable {
  return structuredClone(initialState);
}

interface PresetProps {
  presetId?: string;
  presetData?: Preset;
  presetNames: string[];
  isFetchingPresetList: boolean;
  findReactionSearchPreferences: () => void;
}

function PresetPage(props: PresetProps) {
  const { presetId, presetData, presetNames, isFetchingPresetList, findReactionSearchPreferences } =
    props;

  useEffect(() => {
    findReactionSearchPreferences();
  }, []);

  const presetNotLoaded = presetId && !presetData;

  if (isFetchingPresetList || presetNotLoaded) {
    return <LoadingScreen />;
  }

  return (
    <PresetForm
      key={presetId}
      presetId={presetId}
      presetData={presetId ? presetData : getInitialState()}
      presetNames={presetNames}
    />
  );
}

interface ToolbarContextProps {
  changeLocation: typeof changeLocation;
}

function ToolbarContext(props: ToolbarContextProps) {
  const { changeLocation } = props;

  const dismissAutosavingToast = () => {
    BottomRightToaster.dismiss('autosaving');
  };

  const navigateBack = () => {
    dismissAutosavingToast();
    changeLocation('/dashboard/settings/synthesis-plan-parameters');
  };

  return (
    <Toolbar.Item>
      <Button large minimal text="Back to the list" icon="chevron-left" onClick={navigateBack} />
    </Toolbar.Item>
  );
}

PresetPage.ToolbarContext = _.flowRight([withRouter, connect(null, { changeLocation })])(
  ToolbarContext
);

export default withRouter(
  connect(
    (state: any, ownProps: any) => {
      const { userSearchParametersId } = ownProps.match.params;
      const presets = state.reactionSearchPreference.data as Record<string, Preset>;
      return {
        presetId: userSearchParametersId,
        presetData: presets[userSearchParametersId],
        isFetchingPresetList:
          !state.reactionSearchPreference.find.initialized &&
          state.reactionSearchPreference.find.processing > 0,
        presetNames: Object.values(presets).map(preset => preset.name),
      };
    },
    {
      findReactionSearchPreferences,
    }
  )(PresetPage)
);
